import React, { useState, useEffect } from "react"
import { countryOptionsForSelect } from "../../../constants/CountryPhoneData"
import { getUserDetails, updatePhone } from "../../../apis/UserSessionAPI"
import * as PhoneNumberVerificationAPI from "../../../apis/External/PhoneNumberVerification"

const recaptchaContainerId = "recaptcha-container"

function formatError(ok = false, type = "", message = "") {
  return {
    ok: ok,
    type: type,
    message: message,
  }
}

export default function PhoneDetailsForm(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { state, setState, userToken } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false)

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    async function doGetUserDetails() {
      if (userToken) {
        const user = await getUserDetails(userToken)
        if ("phone" in user) {
          const { phone } = user
          setState({
            ...state,
            isVerificationCompOpen: false,
            isNumberLoaded: true,
            number: phone.number,
            countryCode: phone.country_code,
            countryPrefix: phone.country_prefix,
            isVerified: phone.verified,
            renderPhoneConfirmationHelp: false,
          })
        } else {
          setState({
            ...state,
            isVerificationCompOpen: true,
            isNumberLoaded: true,
          })
        }
      }
    }
    doGetUserDetails()
  }, [])

  useEffect(() => {
    if (waitingForConfirmation) {
      let timer = setTimeout(() => {
        setState({ ...state, renderPhoneConfirmationHelp: true })
      }, 8000)
      return () => {
        clearTimeout(timer)
      }
    }
  }, [waitingForConfirmation])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function handleInputChange(event) {
    setState({
      ...state,
      [event.target.name]: event.target.value,
    })
  }

  function getPhoneInfo(isVerified) {
    return {
      country_code: state.countryCode,
      country_prefix: state.countryPrefix,
      number: state.newNumber,
      verified: isVerified,
    }
  }

  async function handleVerification(event) {
    event.preventDefault()

    if (!state.newNumber || !state.countryCode) {
      setState({
        ...state,
        error: formatError(
          false,
          "sendingSMS",
          I18n.t("cart_summary.phone_verification_check_number")
        ),
      })

      return
    }

    setState({ ...state, isSendingSms: true })
    const completePhoneNumber = state.countryPrefix + state.newNumber

    try {
      await updatePhone(getPhoneInfo(false), userToken)

      await PhoneNumberVerificationAPI.requestSMSCode(
        completePhoneNumber,
        userToken,
        state.countryCode
      )

      setState({ ...state, isSendingSms: false })
      setWaitingForConfirmation(true)
    } catch (error) {
      let errorMessage
      if (error === "taken") {
        errorMessage = formatError(
          false,
          "sendingSMS",
          I18n.t("cart_summary.phone_exists_error")
        )
      } else {
        errorMessage = formatError(
          false,
          "sendingSMS",
          I18n.t("cart_summary.phone_error")
        )
      }
      setState({
        ...state,
        isSendingSms: false,
        error: errorMessage,
      })
    }
  }

  async function handleConfirmation(e) {
    e.preventDefault()
    if (!state.code) {
      setState({
        ...state,
        error: formatError(
          false,
          "codeConfirmation",
          I18n.t("cart_summary.missing_phone_code")
        ),
      })
      return
    }

    const completePhoneNumber = state.countryPrefix + state.newNumber

    setState({ ...state, isConfirmingCode: true })

    // Verify sms code input
    try {
      await PhoneNumberVerificationAPI.verifySMSCode(
        completePhoneNumber,
        state.code,
        userToken
      )
      // SUCCESS: phone verified, update on backend
      const updateRes = await updatePhone(getPhoneInfo(true), userToken)

      setState({
        ...state,
        isConfirmingCode: false,
        error: formatError(true, "", ""),
        needVerification: false,
        number: updateRes.number,
        countryPrefix: updateRes.country_prefix,
        isVerified: updateRes.verified,
      })
      setWaitingForConfirmation(false)
    } catch (error) {
      setState({
        ...state,
        error: formatError(
          false,
          "codeConfirmation",
          I18n.t("cart_summary.phone_confirmation_code_alert")
        ),
        isConfirmingCode: false,
      })
    }
  }

  function toggleVerification() {
    setState({
      ...state,
      needVerification: !state.needVerification,
      needToModify: true,
    })
  }

  function backToVerification() {
    setState({
      ...state,
      needVerification: true,
      error: formatError(true, "", ""),
    })
    setWaitingForConfirmation(false)
  }

  function renderPhoneVerification() {
    return (
      <div>
        <h4>{I18n.t("cart_summary.phone_title")}</h4>
        <p className="checkout-info">
          {I18n.t("cart_summary.phone_verification_info")}{" "}
          {state.needToModify && (
            <a href="#0" onClick={toggleVerification}>
              {I18n.t("cart_summary.back_from_modify")}
            </a>
          )}
        </p>
        <fieldset>
          <div className="row">
            <div className="col-sm-3 phone-input">
              <div className="form-group">
                <label htmlFor="countryPrefix" className="control-label">
                  {"Prefisso"}*
                </label>

                <select
                  className="form-control"
                  name="countryPrefix"
                  value={state.countryPrefix}
                  onChange={handleInputChange}
                >
                  {countryOptionsForSelect}
                </select>
              </div>
            </div>

            <div className="col-sm-5 phone-input">
              <div className="form-group">
                <label htmlFor="newNumber" className="control-label">
                  {I18n.t("cart_summary.phone")}*
                </label>
                <input
                  name="newNumber"
                  type="tel"
                  className="form-control"
                  value={state.newNumber}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="col-sm-3 phone-input">
              <button
                className="btn btn-primary phone-verification-button"
                type="button"
                disabled={state.isSendingSms}
                onClick={handleVerification}
              >
                {state.isSendingSms
                  ? I18n.t("cart_summary.sending")
                  : I18n.t("cart_summary.phone_verify_number")}
              </button>
            </div>
          </div>

          {!state.error.ok && state.error.type === "sendingSMS" && (
            <div className="row">
              <div className="col-sm-12">
                <p className="alert small alert-danger">
                  {state.error.message}
                </p>
              </div>
            </div>
          )}
        </fieldset>
      </div>
    )
  }

  function renderPhoneConfirmation() {
    return (
      <div>
        <h4>{I18n.t("cart_summary.phone_title")}</h4>
        <p className="checkout-info">
          {I18n.t("cart_summary.phone_code_info")}{" "}
          <strong>{`${state.countryPrefix} ${state.newNumber}`}</strong>.
        </p>
        <fieldset>
          <div className="row">
            <div className="col-sm-6 phone-input">
              <div className="form-group">
                <label htmlFor="code" className="control-label">
                  {I18n.t("cart_summary.phone_code")}*
                </label>
                <input
                  name="code"
                  type="number"
                  className="form-control"
                  value={state.code}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="col-sm-3 phone-input">
              <button
                className="btn btn-primary phone-verification-button"
                type="button"
                disabled={state.isConfirmingCode}
                onClick={handleConfirmation}
              >
                {state.isConfirmingCode
                  ? I18n.t("cart_summary.sending")
                  : I18n.t("cart_summary.phone_confirm_code")}
              </button>
            </div>
          </div>
        </fieldset>
        {!state.error.ok && state.error.type === "codeConfirmation" && (
          <div className="row">
            <div className="col-sm-12">
              <p className="alert small alert-danger">{state.error.message}</p>
            </div>
          </div>
        )}
        {state.renderPhoneConfirmationHelp && (
          <p className="checkout-info">
            {I18n.t("cart_summary.phone_code_issues")}.{" "}
            <a href="#0" onClick={backToVerification}>
              {I18n.t("cart_summary.phone_code_issues_send_again")}
            </a>
          </p>
        )}
      </div>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  const {
    isNumberLoaded,
    countryPrefix,
    number,
    isVerified,
    needVerification,
    needToModify,
  } = state
  if (!isNumberLoaded) {
    return (
      <div className="phone-details order-step">
        <h4>{I18n.t("cart_summary.phone_title")}</h4>
        <p className="small">{I18n.t("cart_summary.phone_loading")}</p>
      </div>
    )
  }

  // -------------------------------------

  return (
    <div className="phone-details order-step">
      {isNumberLoaded && number && !needVerification ? (
        <div>
          <h4>{I18n.t("cart_summary.phone_title")}</h4>
          <p className="checkout-info">
            {isVerified
              ? I18n.t("cart_summary.phone_verified_info")
              : I18n.t("cart_summary.phone_not_verified_info")}{" "}
            <a href="#0" onClick={toggleVerification}>
              {I18n.t("cart_summary.modify_phone")}
            </a>
          </p>
          {!isVerified && (
            <div className="row">
              <div className="col-sm-12">
                <p className="alert small alert-danger mt-3">
                  {I18n.t("cart_summary.phone_not_verified_alert")}
                </p>
              </div>
            </div>
          )}
          <p>
            <strong>{`${countryPrefix} ${number}`}</strong>{" "}
            <span className="badge badge-light small">
              {isVerified ? "verificato" : "non verificato"}
            </span>
          </p>
          {!isVerified && (
            <button
              className="btn btn-primary"
              href="#0"
              onClick={toggleVerification}
            >
              {I18n.t("cart_summary.phone_verify_number")}
            </button>
          )}
        </div>
      ) : !waitingForConfirmation ? (
        renderPhoneVerification()
      ) : (
        renderPhoneConfirmation()
      )}
      <div id="recaptcha-container" />
    </div>
  )
}
